/**
 * Loan-level Capital & Economics panel — Finance V2.
 *
 * Shows:
 *   - Capital-source snapshot per tranche (primary funding + each additional advance)
 *   - Attributable Cost of Capital (ACTUAL mode: snapshots only; otherwise unavailable)
 *   - Realized economics: Principal Deployed / Recorded Repayment / Gross Funding Revenue
 *     / Gross Yield / Attributable COC / Net Contribution / Contribution Margin
 *
 * Guardrails:
 *   - Metrics render only when supported by actual recorded data; otherwise shown
 *     as "Unavailable" or "Modeled" with explicit labeling.
 *   - Never silently inherits today's selected Capital Structure for actual math.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../lib/errors";

const SOURCE_LABELS = [
  { v: "company_internal", l: "Company / Internal Capital" },
  { v: "line_of_credit",   l: "Line of Credit" },
  { v: "eam",              l: "EAM" },
  { v: "broker_outside",   l: "Broker / Outside Capital" },
  { v: "custom",           l: "Custom" },
  { v: "other",            l: "Other" },
];
const RATE_TYPES = [
  { v: "fixed",         l: "Fixed annual %" },
  { v: "prime_plus",    l: "Prime + spread" },
  { v: "internal_zero", l: "Internal / Zero (company-funded)" },
  { v: "custom",        l: "Custom" },
];

function fmtMoney(v) {
  if (v == null) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}
function fmtMoney2(v) {
  if (v == null) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPct(v) {
  if (v == null) return "—";
  return `${(v * 100).toFixed(2)}%`;
}

function Pill({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-[#F3F4F6] text-[#374151]",
    green: "bg-[#D1FAE5] text-[#065F46]",
    amber: "bg-[#FEF3C7] text-[#78350F]",
    red:   "bg-[#FEE2E2] text-[#991B1B]",
    blue:  "bg-[#DBEAFE] text-[#1E3A8A]",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${tones[tone]}`}>{children}</span>;
}

export default function LoanCapitalEconomics({ loanId }) {
  const [structures, setStructures] = useState([]);
  const [tranches, setTranches] = useState([]);
  const [actual, setActual] = useState(null);     // actual-mode unit econ
  const [modeled, setModeled] = useState(null);   // modeled-mode unit econ (for reference)
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);  // {kind:'primary'|'advance', idx?:n}
  const [formData, setFormData] = useState(blankForm());

  function blankForm() {
    return {
      structure_id: "",
      source_label: "company_internal",
      structure_name: "",
      rate_type: "fixed",
      rate_type_detail: "",
      annual_cost_of_capital_pct: 0,
      origination_fee_pct: 0,
      servicing_fee_pct_annual: 0,
      fixed_annual_fee: 0,
      notes: "",
    };
  }

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: structRes }, { data: snapRes }, { data: actRes }, { data: modRes }] = await Promise.all([
        api.get("/finance/v2/capital-structures"),
        api.get(`/finance/v2/loans/${loanId}/capital-source`),
        api.get(`/finance/v2/loans/${loanId}/unit-economics`, { params: { mode: "actual" } }),
        api.get(`/finance/v2/loans/${loanId}/unit-economics`, { params: { mode: "modeled" } }).catch(() => ({ data: null })),
      ]);
      setStructures(structRes.structures || []);
      setTranches(snapRes.tranches || []);
      setActual(actRes);
      setModeled(modRes);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [loanId]);

  const applyStructureSeed = (sid) => {
    const s = structures.find((x) => x.id === sid);
    if (!s) {
      setFormData((d) => ({ ...d, structure_id: "", structure_name: "" }));
      return;
    }
    setFormData((d) => ({
      ...d,
      structure_id: s.id,
      structure_name: s.name,
      annual_cost_of_capital_pct: Number(s.annual_cost_of_capital_pct || 0),
      origination_fee_pct: Number(s.origination_fee_pct || 0),
      servicing_fee_pct_annual: Number(s.servicing_fee_pct_annual || 0),
      fixed_annual_fee: Number(s.fixed_annual_fee || 0),
    }));
  };

  const save = async () => {
    try {
      const payload = { ...formData };
      // Internal zero convenience: force COC pct to 0 when rate_type is internal_zero
      if (payload.rate_type === "internal_zero") payload.annual_cost_of_capital_pct = 0;
      if (!payload.structure_id) delete payload.structure_id;
      const url = (showForm.kind === "primary")
        ? `/finance/v2/loans/${loanId}/capital-source`
        : `/finance/v2/loans/${loanId}/advances/${showForm.idx}/capital-source`;
      await api.post(url, payload);
      toast.success("Capital source saved");
      setShowForm(null); setFormData(blankForm());
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearSnapshot = async (tranche) => {
    try {
      const url = tranche.advance_idx == null
        ? `/finance/v2/loans/${loanId}/capital-source`
        : `/finance/v2/loans/${loanId}/advances/${tranche.advance_idx}/capital-source`;
      await api.delete(url);
      toast.success("Snapshot cleared");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading && !actual) return <div className="text-sm text-[#6B7280]">Loading capital/economics…</div>;

  const dep = actual?.deployed || {};
  const econ = actual?.settlement || {};
  const coc = actual?.cost_of_capital || {};
  const con = actual?.contribution || {};
  const cocModeled = modeled?.cost_of_capital || {};
  const conModeled = modeled?.contribution || {};

  // Gross Funding Revenue = max(0, recorded - deployed) — already computed server-side
  const grossRev = econ.realized_funding_revenue;
  const principalDeployed = econ.principal_deployed ?? dep.deployed;
  const grossYield = (grossRev != null && principalDeployed > 0) ? grossRev / principalDeployed : null;
  const actualAvailable = coc?.status === "ok" && con?.net_portfolio_contribution != null;

  return (
    <div className="space-y-6" data-testid="loan-capital-economics">
      {/* Capital Source Snapshots */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-semibold text-[#0A2540]">Capital source — per tranche</div>
            <div className="text-[11px] text-[#6B7280]">Snapshots preserve the economics at the time of each advance. Later edits to the master Capital Structure do not retroactively change these.</div>
          </div>
        </div>
        <div className="rounded-md border border-[#E5E7EB] overflow-hidden">
          {tranches.length === 0 && <div className="px-4 py-3 text-sm text-[#6B7280]">No deployment events found.</div>}
          {tranches.map((t) => (
            <div key={t.event_source} className="flex items-center justify-between px-4 py-3 border-b border-[#F3F4F6] last:border-b-0">
              <div className="flex-1">
                <div className="text-sm text-[#0A2540] flex items-center gap-2">
                  <span className="font-medium">{t.event_source === "primary_funding" ? "Primary funding" : `Additional advance #${t.advance_idx + 1}`}</span>
                  <span className="text-[#6B7280]">· {t.date || "—"} · {fmtMoney(t.amount)}</span>
                  {t.status === "assigned" ? <Pill tone="green">Snapshot attached</Pill> : <Pill tone="amber">Unassigned</Pill>}
                </div>
                {t.capital_source && (
                  <div className="text-[11px] text-[#4B5563] mt-1">
                    {t.capital_source.structure_name || t.capital_source.source_label} · {t.capital_source.annual_cost_of_capital_pct}% ·
                    rate type: {t.capital_source.rate_type}{t.capital_source.rate_type_detail ? ` (${t.capital_source.rate_type_detail})` : ""} ·
                    captured {(t.capital_source.captured_at || "").slice(0, 10)} by {t.capital_source.captured_by}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setFormData(blankForm()); setShowForm({ kind: t.advance_idx == null ? "primary" : "advance", idx: t.advance_idx }); }}
                        className="text-xs h-7 px-2.5 rounded-md border border-[#D1D5DB] bg-white hover:bg-[#F3F4F6]"
                        data-testid={`attach-source-${t.event_source}`}>
                  {t.status === "assigned" ? "Edit" : "Attach"}
                </button>
                {t.status === "assigned" && (
                  <button onClick={() => clearSnapshot(t)}
                          className="text-xs h-7 px-2.5 rounded-md border border-[#D1D5DB] bg-white hover:bg-[#FEE2E2]"
                          data-testid={`clear-source-${t.event_source}`}>Clear</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Economic metrics */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-[#0A2540]">Economics</div>
          {actualAvailable
            ? <Pill tone="green">Actual · from snapshots</Pill>
            : (coc?.status === "unavailable" || coc?.has_unassigned_tranche)
              ? <Pill tone="amber">Actual COC unavailable (unassigned tranches)</Pill>
              : <Pill tone="slate">Settlement not realized</Pill>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric label="Principal Deployed" value={fmtMoney2(principalDeployed)} />
          <Metric label="Recorded Repayment" value={econ.recorded_settlement != null ? fmtMoney2(econ.recorded_settlement) : "—"}
                  hint={econ.status !== "simple" ? `Attribution: ${econ.status || "pending"}` : ""} />
          <Metric label="Gross Funding Revenue" value={grossRev != null ? fmtMoney2(grossRev) : "—"} />
          <Metric label="Gross Yield" value={fmtPct(grossYield)} hint="Revenue ÷ principal · pre-COC" />
          <Metric label="Attributable Cost of Capital"
                  value={coc?.total_cost_of_capital != null ? fmtMoney2(coc.total_cost_of_capital) : "Unavailable"}
                  tone={coc?.total_cost_of_capital == null ? "amber" : "slate"}
                  hint={coc?.provenance ? `Provenance: ${coc.provenance}` : ""} />
          <Metric label="Net Contribution"
                  value={con?.net_portfolio_contribution != null ? fmtMoney2(con.net_portfolio_contribution) : "Unavailable"}
                  tone={con?.net_portfolio_contribution == null ? "amber" : "slate"} />
          <Metric label="Contribution Margin"
                  value={con?.contribution_margin_pct != null ? fmtPct(con.contribution_margin_pct) : "Unavailable"}
                  hint="Net Contribution ÷ Realized Funding Revenue" />
          <Metric label="Net Return on Deployed"
                  value={con?.net_return_on_deployed != null ? fmtPct(con.net_return_on_deployed) : "Unavailable"} />
        </div>

        {/* Modeled reference (never labeled realized) */}
        {!actualAvailable && modeled && conModeled?.net_portfolio_contribution != null && (
          <div className="mt-3 rounded-md border border-dashed border-[#D1D5DB] bg-[#FAFAFA] p-3" data-testid="modeled-reference">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Modeled reference — not realized economics</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-[#374151]">
              <div>COC (modeled): <span className="font-medium">{fmtMoney2(cocModeled.total_cost_of_capital)}</span></div>
              <div>Net contribution (modeled): <span className="font-medium">{fmtMoney2(conModeled.net_portfolio_contribution)}</span></div>
              <div>Margin (modeled): <span className="font-medium">{fmtPct(conModeled.contribution_margin_pct)}</span></div>
              <div>Structure: <span className="font-medium">{modeled.cost_of_capital?.structure_name || "—"}</span></div>
            </div>
            <div className="text-[11px] text-[#6B7280] mt-2">Attach capital-source snapshots to each tranche above to replace these modeled figures with actual.</div>
          </div>
        )}
      </div>

      {/* Attach / Edit modal (inline) */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowForm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-[520px] p-5" data-testid="capital-source-form">
            <div className="font-semibold text-[#0A2540] text-sm mb-3">
              Attach capital source — {showForm.kind === "primary" ? "Primary funding" : `Additional advance #${showForm.idx + 1}`}
            </div>
            <div className="space-y-3">
              <Field label="Seed from existing structure (optional)">
                <select value={formData.structure_id} onChange={(e) => applyStructureSeed(e.target.value)}
                        className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md bg-white">
                  <option value="">— No seed, enter custom —</option>
                  {structures.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.annual_cost_of_capital_pct}%</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Source label">
                  <select value={formData.source_label} onChange={(e) => setFormData((d) => ({ ...d, source_label: e.target.value }))}
                          className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md bg-white">
                    {SOURCE_LABELS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </Field>
                <Field label="Rate type">
                  <select value={formData.rate_type} onChange={(e) => setFormData((d) => ({ ...d, rate_type: e.target.value }))}
                          className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md bg-white">
                    {RATE_TYPES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Structure name (what this money is from)">
                <input value={formData.structure_name} onChange={(e) => setFormData((d) => ({ ...d, structure_name: e.target.value }))}
                       placeholder="e.g., LOC @ Prime+1, EAM Facility A, Internal Capital"
                       className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Annual COC %">
                  <input type="number" step="0.01" value={formData.annual_cost_of_capital_pct}
                         onChange={(e) => setFormData((d) => ({ ...d, annual_cost_of_capital_pct: Number(e.target.value) }))}
                         disabled={formData.rate_type === "internal_zero"}
                         className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md" />
                </Field>
                <Field label="Rate type detail (optional)">
                  <input value={formData.rate_type_detail} onChange={(e) => setFormData((d) => ({ ...d, rate_type_detail: e.target.value }))}
                         placeholder="e.g., Prime+1.00 as of 2026-02-19"
                         className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md" />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Origination fee %">
                  <input type="number" step="0.01" value={formData.origination_fee_pct}
                         onChange={(e) => setFormData((d) => ({ ...d, origination_fee_pct: Number(e.target.value) }))}
                         className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md" />
                </Field>
                <Field label="Servicing %/yr">
                  <input type="number" step="0.01" value={formData.servicing_fee_pct_annual}
                         onChange={(e) => setFormData((d) => ({ ...d, servicing_fee_pct_annual: Number(e.target.value) }))}
                         className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md" />
                </Field>
                <Field label="Fixed $/yr">
                  <input type="number" step="1" value={formData.fixed_annual_fee}
                         onChange={(e) => setFormData((d) => ({ ...d, fixed_annual_fee: Number(e.target.value) }))}
                         className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md" />
                </Field>
              </div>
              <Field label="Notes (optional)">
                <input value={formData.notes} onChange={(e) => setFormData((d) => ({ ...d, notes: e.target.value }))}
                       className="h-9 w-full px-2 text-sm border border-[#E5E7EB] rounded-md" />
              </Field>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowForm(null)} className="h-9 px-3 text-sm rounded-md border border-[#D1D5DB] bg-white"
                      data-testid="capital-source-cancel">Cancel</button>
              <button onClick={save} className="h-9 px-3 text-sm rounded-md bg-[#0066FF] text-white hover:bg-[#0052CC]"
                      data-testid="capital-source-save">Save snapshot</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, hint, tone = "slate" }) {
  const tones = { slate: "text-[#0A2540]", amber: "text-[#78350F]", green: "text-[#065F46]" };
  return (
    <div className="rounded-md border border-[#E5E7EB] p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</div>
      <div className={`text-base font-semibold tabular-nums mt-0.5 ${tones[tone]}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#6B7280] mt-0.5">{hint}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      {children}
    </div>
  );
}
